import dayjs from 'dayjs';
import { useCallback } from 'react';
import { AgencyTaskSchema } from '@ametista-ai/api-schema';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Group, MultiSelect, Stack, TextInput } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { UserSelector } from '@/components/Users/UserSelector';

export function PostChecklistItemForm({
  value,
  onChange,
  onCancel,
  tasks,
  isSaving,
}: {
  value?: AgencyTaskSchema;
  onChange: (item: Partial<AgencyTaskSchema>) => void;
  onCancel: () => void;
  tasks: AgencyTaskSchema[];
  isSaving?: boolean;
}) {
  const { t } = useTranslation();
  const form = useForm({
    mode: 'controlled',
    initialValues: {
      title: value?.title ?? '',
      dueDate: value?.dueDate ? dayjs(value.dueDate) : null,
      userId: value?.userId,
      blockedBy: value?.blockedBy ?? [],
      blockedByPostApproval: value?.blockedByPostApproval ?? false,
    },
    validate: {
      title: (value) => (value.length > 0 ? null : t('posts.title-required')),
    },
  });

  const handleSubmit = useCallback(
    async (values: typeof form.values) => {
      onChange({
        ...values,
        userId: values.userId,
        dueDate: values.dueDate ? dayjs(values.dueDate).toDate() : null,
        blockedBy: values.blockedBy,
        blockedByPostApproval: values.blockedByPostApproval,
      });
    },
    [form, onChange]
  );

  const handleCancel = useCallback(() => {
    form.reset();
    onCancel();
  }, [form, onCancel]);

  const otherTasks = (tasks ?? [])
    .filter((t) => t.id !== value?.id)
    .map((item) => ({
      label: item.title,
      value: item.id,
    }));

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} style={{ flex: 1 }}>
      <Stack gap="xs">
        <Group gap="xs">
          <TextInput
            size="xs"
            flex={1}
            placeholder={t('posts.title')}
            autoFocus
            disabled={isSaving}
            {...form.getInputProps('title')}
          />
          <DateTimePicker
            size="xs"
            placeholder={t('posts.due-date')}
            w={150}
            clearable
            disabled={isSaving}
            {...form.getInputProps('dueDate')}
          />
        </Group>
        <Group gap="xs">
          <UserSelector
            size="xs"
            placeholder={t('posts.select-owner')}
            w={170}
            clearable
            disabled={isSaving}
            {...form.getInputProps('userId')}
          />
          <MultiSelect
            size="xs"
            placeholder={t('clients.blocked-by')}
            data={otherTasks}
            value={form.values.blockedBy}
            onChange={(value) => form.setFieldValue('blockedBy', value)}
            disabled={isSaving}
          />
        </Group>
        {!value?.assignedToClient && (
          <Checkbox
            size="xs"
            label={t('posts.wait-for-client-approval')}
            disabled={isSaving}
            {...form.getInputProps('blockedByPostApproval', { type: 'checkbox' })}
          />
        )}
        <Group gap="xs">
          <Button size="compact-sm" type="submit" variant="primary" loading={isSaving}>
            {t('posts.save')}
          </Button>
          <Button size="compact-sm" variant="subtle" onClick={handleCancel} disabled={isSaving}>
            {t('posts.cancel')}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
